// shared/src/news/news-relevance-store.ts
// D1 store for the append-only `news_relevance` table (migrations/0121_news_relevance.sql).
// Central news pool P2: the per-brief MATERIALIZED per-ticker slice + citation feedback. Written by
// the p-stage per-ticker read (fetchTickerNews) the moment it selects a ticker's slice; the `cited`
// flag is filled by the P2b citation loop; the F tab (P2c) reads it back as exact provenance of what
// each ticker's analysts were shown.
//
// Idempotency: PK (brief_id, news_item_id, ticker) with INSERT OR IGNORE — the same brief re-reads a
// ticker's slice deterministically (the lean pass materializes all tickers; the deep pass re-reads 6
// portfolio tickers), so a re-write of the identical row is a no-op, not a duplicate. The write is
// best-effort at the call site (a materialization-log failure must NEVER fail a brief).

import { z } from 'zod';

// Structural D1 subset — same pattern as news-items-store.ts.
export interface NewsRelevanceD1 {
  prepare(sql: string): NewsRelevanceD1Stmt;
  batch(statements: NewsRelevanceD1Stmt[]): Promise<unknown[]>;
}
export interface NewsRelevanceD1Stmt {
  bind(...values: unknown[]): NewsRelevanceD1Stmt;
  all<T = unknown>(): Promise<{ results: T[]; success: boolean }>;
  run(): Promise<unknown>;
}

/** Which relevance layer pulled the item into the ticker's slice (mirrors RelevanceSource). */
export type NewsRelevanceSource = 'direct' | 'peer' | 'driver' | 'question';

/** A materialized-slice row. Mirrors the 0121 DDL columns exactly (schema-vs-ddl test). */
export interface NewsRelevanceRow {
  readonly brief_id:     string;
  readonly news_item_id: string;
  readonly ticker:       string;
  readonly source:       NewsRelevanceSource;
  readonly score:        number;
  readonly cited:        number;   // 0 | 1
  readonly created_at:   string;   // ISO-Z
}

export const NewsRelevanceRowSchema = z.object({
  brief_id:     z.string(),
  news_item_id: z.string(),
  ticker:       z.string(),
  source:       z.enum(['direct', 'peer', 'driver', 'question']),
  score:        z.number(),
  cited:        z.number().int().min(0).max(1),
  created_at:   z.string(),
});

const COLS = 'brief_id, news_item_id, ticker, source, score, cited, created_at';
const INSERT_SQL = `INSERT OR IGNORE INTO news_relevance (${COLS}) VALUES (?, ?, ?, ?, ?, ?, ?)`;

/** Append materialized-slice rows (idempotent INSERT OR IGNORE, batched). No-op on empty. Callers
 *  wrap best-effort — a news_relevance write must NEVER fail a brief. */
export async function writeNewsRelevance(db: NewsRelevanceD1, rows: ReadonlyArray<NewsRelevanceRow>): Promise<void> {
  if (rows.length === 0) return;
  const stmt = db.prepare(INSERT_SQL);
  const inserts = rows.map((r) => stmt.bind(r.brief_id, r.news_item_id, r.ticker, r.source, r.score, r.cited, r.created_at));
  await db.batch(inserts);
}

/** Read a brief's materialized slice (the F tab's exact "what each ticker saw" provenance).
 *  Ordered ticker then score-desc. */
export async function readNewsRelevanceForBrief(db: NewsRelevanceD1, briefId: string): Promise<NewsRelevanceRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM news_relevance WHERE brief_id = ? ORDER BY ticker ASC, score DESC`)
    .bind(briefId)
    .all<NewsRelevanceRow>();
  return res.results ?? [];
}

/** A brief's slice rows joined to the source item title — the bridge the citation loop (P2b) needs
 *  to match a cited FED item (which carries a title, not an id) back to its (news_item_id, ticker),
 *  and the by-target provenance the /oracle News tab (P2c-2) renders (source layer + cited flag). */
export interface BriefSliceItem {
  readonly news_item_id: string;
  readonly ticker:       string;
  readonly title:        string;
  readonly source:       NewsRelevanceSource;   // the relevance layer that pulled it in
  readonly cited:        number;                // 0 | 1 (0 until the P2b citation loop marks it)
}

/** Read a brief's slice with each item's news_items.title (INNER JOIN — an orphaned relevance row
 *  with no surviving news_items row is skipped, which is correct: we can only mark citations for
 *  items still in the corpus). Ordered ticker then score-desc. */
export async function readBriefSliceItems(db: NewsRelevanceD1, briefId: string): Promise<BriefSliceItem[]> {
  const res = await db
    .prepare(
      `SELECT nr.news_item_id AS news_item_id, nr.ticker AS ticker, ni.title AS title,
              nr.source AS source, nr.cited AS cited
         FROM news_relevance nr JOIN news_items ni ON ni.id = nr.news_item_id
        WHERE nr.brief_id = ? ORDER BY nr.ticker ASC, nr.score DESC`,
    )
    .bind(briefId)
    .all<BriefSliceItem>();
  return res.results ?? [];
}

/** The most recent brief that materialized a slice into news_relevance (by created_at). NULL when the
 *  table is empty (no brief has run since P2a shipped). The /oracle News tab reads THIS brief's slice
 *  for its provenance overlay. */
export async function readLatestRelevanceBriefId(db: NewsRelevanceD1): Promise<string | null> {
  const res = await db
    .prepare('SELECT brief_id FROM news_relevance ORDER BY created_at DESC LIMIT 1')
    .all<{ brief_id: string }>();
  return res.results?.[0]?.brief_id ?? null;
}

/** Mark the given (news_item_id, ticker) rows of a brief as cited (P2b citation loop). Idempotent
 *  (re-running re-sets cited=1). Batched; no-op on empty. */
export async function updateNewsRelevanceCited(
  db:      NewsRelevanceD1,
  briefId: string,
  keys:    ReadonlyArray<{ news_item_id: string; ticker: string }>,
): Promise<void> {
  if (keys.length === 0) return;
  const stmt = db.prepare('UPDATE news_relevance SET cited = 1 WHERE brief_id = ? AND news_item_id = ? AND ticker = ?');
  await db.batch(keys.map((k) => stmt.bind(briefId, k.news_item_id, k.ticker)));
}

/** Empirical utility of each relevance layer = cite-rate over recent materialized slices. The signal
 *  the ranker will eventually be tuned on (retire-bar / equivalence-watch); surfaced on the F tab
 *  (P2c). `total` 0 for a source = no data yet → the caller treats it as neutral, never a fabricated
 *  rate. Window-bounded by created_at (ISO-Z; C018). */
export interface SourceCiteRate {
  readonly source: NewsRelevanceSource;
  readonly total:  number;
  readonly cited:  number;
  readonly rate:   number;   // cited/total, or 0 when total=0 (neutral — caller must gate on total)
}

export async function readCiteRateBySource(db: NewsRelevanceD1, sinceIso: string): Promise<SourceCiteRate[]> {
  const res = await db
    .prepare(
      `SELECT source, COUNT(*) AS total, SUM(cited) AS cited
         FROM news_relevance WHERE created_at >= ? GROUP BY source`,
    )
    .bind(sinceIso)
    .all<{ source: NewsRelevanceSource; total: number; cited: number }>();
  const bySource = new Map((res.results ?? []).map((r) => [r.source, r]));
  const ALL: NewsRelevanceSource[] = ['direct', 'peer', 'driver', 'question'];
  return ALL.map((source) => {
    const r = bySource.get(source);
    const total = r?.total ?? 0;
    const cited = r?.cited ?? 0;
    return { source, total, cited, rate: total > 0 ? cited / total : 0 };
  });
}
